/**
 * calendarGrid.ts — 월 달력 격자(항상 6주 = 42칸) 계산. 순수 함수만(Firebase import 금지).
 *
 * 대시보드 캘린더 위젯(pages/Dashboard.tsx CalendarWidget)에 있던 계산을 그대로 옮겼다(2026-10-19).
 * 내 업무 › 일정·할 일(pages/MySpace/Tasks.tsx) 월 캘린더도 같은 격자를 쓴다.
 *   - 앞은 이전 달 말일부터, 뒤는 다음 달 1일부터 채워 6행 고정 → 달을 넘겨도 높이가 안 흔들린다.
 */

export const CAL_WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"] as const;
export const CAL_ROWS = 6;

const pad2 = (n: number) => String(n).padStart(2, "0");
/** (2026, 10, 9) → "2026-10-09" */
export const calYmd = (y: number, m: number, d: number) => `${y}-${pad2(m)}-${pad2(d)}`;

export interface CalCell {
  /** 그 달 안의 날짜(1~31) — outside 면 이웃 달의 날짜 */
  day: number;
  /** -1 = 이전 달 / 0 = 이번 달 / 1 = 다음 달 */
  outside: -1 | 0 | 1;
  date: string;
}

/** y년 m월(1~12) 달력 42칸 */
export function buildMonthCells(y: number, m: number): CalCell[] {
  const dim = new Date(y, m, 0).getDate();
  const firstDow = new Date(y, m - 1, 1).getDay(); // 0=일
  const prev = m === 1 ? { y: y - 1, m: 12 } : { y, m: m - 1 };
  const next = m === 12 ? { y: y + 1, m: 1 } : { y, m: m + 1 };
  const pdim = new Date(prev.y, prev.m, 0).getDate();
  const out: CalCell[] = [];
  for (let i = firstDow; i > 0; i--) {
    const d = pdim - i + 1;
    out.push({ day: d, outside: -1, date: calYmd(prev.y, prev.m, d) });
  }
  for (let d = 1; d <= dim; d++) out.push({ day: d, outside: 0, date: calYmd(y, m, d) });
  for (let d = 1; out.length < CAL_ROWS * 7; d++) out.push({ day: d, outside: 1, date: calYmd(next.y, next.m, d) });
  return out;
}

/** { y, m } 에서 delta 달 이동 */
export function shiftMonth(v: { y: number; m: number }, delta: number): { y: number; m: number } {
  const idx = v.m - 1 + delta;
  return { y: v.y + Math.floor(idx / 12), m: ((idx % 12) + 12) % 12 + 1 };
}
